import { Collapsible } from '@ui/animated/Collapsible'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Border from '@ui/effects/Border'
import Gradient from '@ui/effects/Gradient'
import PressableIcon from '@ui/buttons/PressableIcon'
import { colors } from '@ui/colors'
import Icon from '@ui/elements/Icon'
import Loader from '@ui/elements/Loader'
import Price from '@/price/Price'
import Status from '@ui/elements/Status'
import Checkbox from '@ui/forms/Checkbox'
import { Preview } from '@order/modals/Summary/SummaryModal'
import { CartItem } from '@order/hooks/useCart'
import { useRemoteOnlyTotal } from '@order/hooks/useOrderTotal'
import { PaymentDetails } from '@order/hooks/usePayments'
import usePaymentStatusPoll from '@order/hooks/usePaymentStatusPoll'
import { useTotalDiscountAmount } from '@order/useDiscounts'
import Typography from '@ui/typography'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, Pressable, View } from 'react-native'
import {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import cn from '@ui/utils/cn'
import { hex } from '@ui/utils/hex'
import { lineBreakToSpace } from 'utils/string'
import OrderGlasses from './OrderGlasses'
import Separator from './Separator'
import {
  borderRadius,
  colorActive,
  colorPayment,
  sContainer,
  sInner,
  statusIconProps,
  statusToBadgeVariant,
  statusToIcon,
} from './styles'
import { usePaymentMethodSettings } from '@order/hooks/usePaymentMethods'
import useModalDataState from '@order/hooks/useModalDataState'
import PaymentDetailsModal from 'features/payment/modals/PaymentDetails/PaymentDetailsModal'

interface Props {
  value: boolean
  data: CartItem
  onChange: () => void
  setPreview: (args: Preview) => void
  infoComponent: React.ReactNode
  expanded?: boolean
}

const OrderStatusPoll = ({ payment }: { payment: PaymentDetails }) => {
  usePaymentStatusPoll(payment, 10_000)
  return null
}

export default function OrderCheckbox({
  data,
  value,
  onChange,
  setPreview,
  infoComponent,
  ...props
}: Props) {
  const { id, result, discounts, order, payment, deals, primaryId } = data
  const { t } = useTranslation(['ui', 'status'])
  const isSelected = value
  const dealName = deals?.base?.dealName || t('common:deal')

  const paymentModal = useModalDataState<PaymentDetails>({
    data: payment || ({} as PaymentDetails),
    isOpen: false,
  })

  const total = useRemoteOnlyTotal({
    result,
    deals,
  })
  const discountAmount = useTotalDiscountAmount({
    orderTotal: total,
    discounts,
  })

  const totalPrice = discountAmount.discounted

  const [expanded, setExpanded] = useState(props.expanded || false)

  const { animation, onPressIn, onPressOut } = useAnimatedPress()
  const checkedValue = useSharedValue(isSelected ? 1 : 0)

  const onPress = () => {
    onChange()
  }

  const styleActive = useAnimatedStyle(() => ({
    opacity: interpolate(checkedValue.value, [0, 1], [0, 1]),
  }))

  const stylePress = useAnimatedStyle(() => ({
    opacity: interpolate(animation.value, [0, 1], [0, 1]),
  }))

  const settings = usePaymentMethodSettings(payment?.method || undefined, {
    provider: payment?.provider || undefined,
  })

  useEffect(() => {
    checkedValue.set(withSpring(isSelected ? 1 : 0))
  }, [isSelected, checkedValue])

  const isPollUpdate = payment?.method ? settings?.updates === 'poll' : false

  return (
    <>
      {payment && isPollUpdate && <OrderStatusPoll payment={payment} />}
      <Pressable
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={!!payment}
      >
        <>
          {!payment && (
            <Gradient
              colors={[colors.gray[75], colors.white25, colors.white]}
              start={[0, 0.5]}
              end={[1, 0.5]}
            />
          )}
          <Border
            style={[
              styleActive,
              { borderRadius, borderColor: hex(colorActive, 0.5) },
            ]}
            className="border-2"
          />
          {primaryId && <Separator />}
          <Gradient
            colors={[hex(colorActive, 0.1), colors.white25, colors.white75]}
            start={[0, 0.5]}
            end={[1, 0.5]}
            style={[styleActive, { borderRadius }]}
          />
          <Gradient
            colors={[
              colors.blue[100],
              colors.blue[50],
              colors.white0,
              colors.white,
            ]}
            start={[0, 0.5]}
            end={[1, 0.5]}
            style={[stylePress, { borderRadius }]}
          />
        </>
        <View className={sContainer}>
          <View className={cn(sInner, 'flex-1')}>
            {!payment && (
              <View className="flex h-8 w-8 items-center justify-center">
                <Checkbox
                  icon="plus"
                  noninteractive
                  value={isSelected}
                  colorActive={colorActive}
                />
              </View>
            )}
            {payment && (
              <Pressable onPress={() => paymentModal.open(payment)}>
                {payment.status !== 'waiting' && (
                  <Icon
                    name={statusToIcon[payment.status]}
                    {...statusIconProps}
                  />
                )}
                {payment.status === 'waiting' && (
                  <Loader loading iconProps={statusIconProps} />
                )}
              </Pressable>
            )}

            <View className="flex flex-row items-center gap-0">
              <Typography
                variant="MDB"
                color={payment ? colorPayment : colors.text}
                numberOfLines={1}
              >
                {lineBreakToSpace(dealName)}
              </Typography>
              {/* <Typography variant="XS">{id}, {primaryId}</Typography> */}
              {infoComponent}
            </View>
          </View>
          <View
            className={cn(
              sInner,
              Platform.select({ default: 'gap-4', web: 'gap-8' })
            )}
          >
            <View className="flex h-max flex-row items-center gap-0">
              {payment && (
                <Pressable onPress={() => paymentModal.open(payment)}>
                  <View className="flex flex-row items-center justify-center">
                    <Status
                      variant={statusToBadgeVariant[payment.status]}
                      text={t(`status:payment.new.${payment.status}.short`)}
                    />
                  </View>
                </Pressable>
              )}
              {total !== undefined && (
                <Pressable
                  onPress={() => payment && paymentModal.open(payment)}
                  className="flex flex-row items-center justify-end"
                  style={{
                    minWidth: 114,
                  }}
                >
                  <View
                    className={cn(
                      'flex h-8 items-center justify-center rounded-sm px-4',
                      !payment && 'bg-gray-100'
                    )}
                    style={{
                      minHeight: 24,
                      display: 'flex',
                      justifyContent: 'center',
                    }} // ensure minHeight matches badge
                  >
                    <Typography
                      variant="MDB"
                      color={payment ? colorPayment : colors.text}
                    >
                      <Price showZero value={totalPrice} />
                    </Typography>
                  </View>
                </Pressable>
              )}
            </View>
            <PressableIcon
              hitSlop={8}
              name={expanded ? 'chevron-up' : 'chevron-down'}
              onPress={() => setExpanded(!expanded)}
            />
          </View>
        </View>
        <Collapsible fullWidth open={expanded}>
          <View className="flex flex-col">
            {order.items.map((item, index) => (
              <OrderGlasses
                key={item.id}
                number={index + 1}
                data={item}
                onPress={() =>
                  setPreview({
                    orderId: id,
                    itemId: item.id,
                  })
                }
              />
            ))}
          </View>
          <View
            className="absolute h-full bg-text"
            style={{
              width: 2,
              top: -0,
              right: 56,
              transform: [{ translateX: 0 }],
            }}
          />
        </Collapsible>
      </Pressable>
      <PaymentDetailsModal data={data} paymentModal={paymentModal} />
    </>
  )
}
